import { apiFetch } from '@/lib/api';

export interface Role {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  permissions: string[];
  created_at: string;
  updated_at: string;
}

export interface PermissionCategory {
  category: string;
  permissions: Array<{
    value: string;
    label: string;
    description: string;
    granularOf?: string;
  }>;
}

export class RolesService {
  /**
   * Get all roles for the current organization
   */
  async getRoles(): Promise<Role[]> {
    const response = await apiFetch('/api/roles');
    if (!response.ok) {
      const error = new Error(`Failed to load roles (${response.status})`);
      throw error;
    }
    const json = await response.json();
    return json.data;
  }

  /**
   * Get a single role by ID
   */
  async getRole(roleId: string): Promise<Role> {
    const response = await apiFetch(`/api/roles/${roleId}`);
    const json = await response.json();
    return json.data;
  }

  /**
   * Create a new custom role
   */
  async createRole(data: {
    name: string;
    description?: string;
    permissions: string[];
  }): Promise<Role> {
    const response = await apiFetch('/api/roles', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await response.json();
    return json.data;
  }

  /**
   * Update a role
   */
  async updateRole(
    roleId: string,
    data: {
      name?: string;
      description?: string;
      permissions?: string[];
    }
  ): Promise<Role> {
    const response = await apiFetch(`/api/roles/${roleId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const json = await response.json();
    return json.data;
  }

  /**
   * Delete a role
   */
  async deleteRole(roleId: string): Promise<void> {
    await apiFetch(`/api/roles/${roleId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get all available permissions with metadata
   */
  async getAvailablePermissions(): Promise<PermissionCategory[]> {
    const response = await apiFetch('/api/permissions/available');
    const json = await response.json();
    return json.data;
  }

  /**
   * Assign roles to a user
   */
  async assignRolesToUser(
    orgId: string,
    userId: string,
    roleIds: string[]
  ): Promise<void> {
    await apiFetch(`/api/orgs/${orgId}/members/${userId}/roles`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roleIds }),
    });
  }

  /**
   * Assign direct permissions to a user
   */
  async assignPermissionsToUser(
    orgId: string,
    userId: string,
    permissions: string[]
  ): Promise<void> {
    await apiFetch(`/api/orgs/${orgId}/members/${userId}/permissions`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ permissions }),
    });
  }

  /**
   * Get user's permissions
   */
  async getUserPermissions(orgId: string, userId: string) {
    const response = await apiFetch(`/api/orgs/${orgId}/members/${userId}/permissions`);
    const json = await response.json();
    return json.data;
  }
}

